const INDUSTRY_FACET = 'job_industry';
const OCCUPATIONAL_FIELD_FACET = 'field_job_occupational_field_parents_all';

export interface SelectedFacetBreadcrumb {
  /**
   * The name of the selected facet.
   */
  name: string;
  /**
   * The label shown in the breadcrumb.
   */
  label: string;
  /**
   * The selected filter value as string for query params fallback.
   */
  filter: string;
  /**
   * Optional SEO alias for path-based breadcrumbs.
   */
  alias?: string;
}

/**
 * Creates a selected facet breadcrumb object.
 *
 * @param params
 *   Facet breadcrumb details.
 * @returns
 *   Normalized selected facet breadcrumb.
 */
export function createSelectedFacetBreadcrumb({
  name,
  label,
  filter,
  alias,
}: SelectedFacetBreadcrumb): SelectedFacetBreadcrumb {
  return {
    name,
    label,
    filter,
    ...(alias ? { alias } : undefined),
  };
}

/**
 * Builds a breadcrumb URL from selected taxonomy facets.
 *
 * @param jobsPage
 *   Jobs listing page path.
 * @param selectedFacets
 *   Selected facet breadcrumbs in hierarchy order.
 * @returns
 *   Alias path when all selected facets have aliases, otherwise
 *   alias path segments plus query params for non-alias facets.
 */
export function getBreadcrumbPath(
  jobsPage: string,
  selectedFacets: SelectedFacetBreadcrumb[],
): string {
  const allSelectedFacetsHaveAliases = selectedFacets.every(
    (selectedFacet) => selectedFacet.alias,
  );

  if (allSelectedFacetsHaveAliases) {
    return `${jobsPage}/${selectedFacets
      .map((selectedFacet) => selectedFacet.alias)
      .join('/')}`;
  }

  const aliasPathSegments = selectedFacets
    .filter((selectedFacet) => selectedFacet.alias)
    .map((selectedFacet) => selectedFacet.alias);

  const basePath = aliasPathSegments.length
    ? `${jobsPage}/${aliasPathSegments.join('/')}`
    : jobsPage;

  const query = new URLSearchParams();
  selectedFacets.forEach((selectedFacet) => {
    if (!selectedFacet.alias) {
      query.append(selectedFacet.name, selectedFacet.filter);
    }
  });

  return `${basePath}?${query.toString()}`;
}

export { INDUSTRY_FACET, OCCUPATIONAL_FIELD_FACET };
